import type { StatusPresentation } from '@/utils/labels';
import type { IntegrationEventView, IntegrationKind, IntegrationView } from '@/types/api';

/** Outside services (ADR-0048) by their own names. */
export const INTEGRATION_NAMES: Record<IntegrationKind, string> = {
  google: 'Google Search Console',
  anthropic: 'Claude',
  ahrefs: 'Ahrefs',
  dataforseo: 'DataForSEO',
  openpagerank: 'Open PageRank',
};

/** What each one is for, in a line. */
export const INTEGRATION_PURPOSE: Record<IntegrationKind, string> = {
  google: 'Подтверждение сайтов, sitemap, поисковые запросы и индексация',
  anthropic: 'Темы и статьи для автоконтента',
  ahrefs: 'Domain Rating и история по месяцам: DR, ссылающиеся домены, трафик',
  dataforseo: 'Ссылки, ссылающиеся домены, спам-скор',
  openpagerank: 'Авторитет домена по графу ссылок, бесплатно',
};

/** The tabs' order: search, the link graph, content. */
export const INTEGRATION_ORDER: readonly IntegrationKind[] = ['google', 'ahrefs', 'dataforseo', 'openpagerank', 'anthropic'];

export const isIntegrationKind = (value: string | undefined): value is IntegrationKind => INTEGRATION_ORDER.includes(value as IntegrationKind);

export const INTEGRATION_STATE: Record<string, StatusPresentation> = {
  ok: { tone: 'healthy', label: 'Работает', glyph: '●', hint: 'Последняя проверка прошла' },
  error: { tone: 'down', label: 'Ошибка', glyph: '■', hint: 'Последняя проверка не прошла' },
  unchecked: { tone: 'pending', label: 'Проверяется', glyph: '◌', hint: 'Проверка ещё не выполнялась' },
  off: { tone: 'unknown', label: 'Не подключено', glyph: '○', hint: 'Ключ не задан' },
};

export type IntegrationState = 'ok' | 'error' | 'unchecked' | 'off';

export function integrationState(view: IntegrationView | undefined): IntegrationState {
  if (!view?.configured) return 'off';
  if (view.checkError) return 'error';
  return view.checkedAt ? 'ok' : 'unchecked';
}

/** The tab's dot for each state. */
export const INTEGRATION_DOT: Record<IntegrationState, 'healthy' | 'down' | 'pending' | 'unknown'> = { ok: 'healthy', error: 'down', unchecked: 'pending', off: 'unknown' };

export const INTEGRATION_EVENT_LABELS: Record<IntegrationEventView['type'], string> = {
  set: 'Ключ или настройки изменены',
  removed: 'Отключено',
  check: 'Проверка',
  scheduled_check: 'Проверка по расписанию',
};

/** How to get each key, step by step: what «Как подключить» lists. */
export const INTEGRATION_STEPS: Record<IntegrationKind, readonly string[]> = {
  google: [
    'Google Cloud Console: создайте проект или выберите существующий.',
    'Включите Google Search Console API и Site Verification API.',
    'IAM → Сервисные аккаунты → создайте аккаунт → Ключи → Добавить ключ → JSON.',
    'Вставьте JSON сюда. Укажите свои Google-аккаунты владельцами — сайты появятся в вашем Search Console.',
    'Сайты подключаются на вкладке SEO сайта: панель добавит запись TXT и отправит sitemap.',
  ],
  anthropic: [
    'console.anthropic.com → API Keys → Create key.',
    'Пополните баланс в разделе Billing.',
    'Выберите модель и бюджет в месяц: при его достижении написание останавливается до следующего месяца.',
  ],
  ahrefs: [
    'Ahrefs → Account settings → API keys → Generate. Ключ выдаёт и бесплатный аккаунт.',
    'Бесплатный ключ даёт текущий Domain Rating всех доменов.',
    'История DR, ссылающихся доменов и трафика по месяцам — с тарифа Lite: около 1 000 единиц на домен в первый раз, потом ~400 в месяц.',
    'Ограничьте расход ключа в Ahrefs (лимит единиц на ключ): панель показывает, сколько израсходовано.',
  ],
  dataforseo: ['app.dataforseo.com → API Access: логин и пароль API.', 'Пополните баланс: оплата за каждый запрос, домен читается раз в неделю.'],
  openpagerank: ['openpagerank.com → регистрация → API key. Бесплатно.'],
};
